// frontend/src/utils/toastUtils.js

import { toast } from 'react-hot-toast';

// Track active toast IDs to prevent duplicates
let activeToasts = new Map();

// Per-message rate limit
let lastErrorTime = 0;

// Global error cooldown — ensures only ONE error toast per window,
// regardless of how many parallel API calls fail or how many code
// paths try to show an error.
let lastErrorShownAt = 0;
const GLOBAL_ERROR_COOLDOWN_MS = 1500;

// ─────────────────────────────────────────────────────────────
// Suppression window — silences the "toast storm" during
// session expiry (10 widgets fail → 1 message, not 10)
// ─────────────────────────────────────────────────────────────
let suppressUntil = 0;

export const suppressErrorToasts = (durationMs = 4000) => {
  suppressUntil = Date.now() + durationMs;
};

export const isSuppressed = () => Date.now() < suppressUntil;

export const clearSuppression = () => {
  suppressUntil = 0;
};

/**
 * Normalize a message into a stable dedupe key.
 * Strips trailing punctuation so "X unavailable" and "X unavailable."
 * both produce the same key.
 */
const normalizeKey = (message) => {
  if (typeof message !== 'string') return JSON.stringify(message);
  return message
    .toLowerCase()
    .trim()
    .replace(/[.!?]+$/g, '')
    .replace(/\s+/g, ' ');
};

/**
 * Show a user-friendly error toast.
 *
 * Layered deduplication:
 *  1. react-hot-toast's built-in `id` — same id = reuse, no duplicate.
 *  2. In-memory active map — same message = reuse.
 *  3. Global cooldown — no error toast within GLOBAL_ERROR_COOLDOWN_MS of
 *     the last one, regardless of message. This is what stops the
 *     "3 identical provider-unavailable toasts" storm.
 *  4. Per-message rate limit — never more than 1 per second.
 */
export const showErrorToast = (message, options = {}) => {
  if (!message || message.trim() === '') return null;
  if (isSuppressed()) return null;

  const now = Date.now();
  const errorKey = normalizeKey(message);
  const stableId = `err_${errorKey}`;

  // Layer 2: same normalized message already showing → reuse
  if (activeToasts.has(errorKey)) {
    return activeToasts.get(errorKey);
  }

  // Layer 3: global cooldown — a recent error toast blocks this one
  if (now - lastErrorShownAt < GLOBAL_ERROR_COOLDOWN_MS) {
    return null;
  }

  // Layer 4: per-message rate limit
  if (now - lastErrorTime < 1000) return null;

  lastErrorTime = now;
  lastErrorShownAt = now;

  // Layer 1: pass a stable `id` to react-hot-toast so it internally
  // deduplicates if the same id fires again within the toast's lifetime.
  const toastId = toast.error(message, {
    id: stableId,
    duration: 4000,
    position: 'top-right',
    style: {
      background: '#FEE2E2',
      color: '#991B1B',
      borderRadius: '12px',
      fontSize: '14px',
      padding: '12px 16px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      maxWidth: '380px',
    },
    iconTheme: { primary: '#DC2626', secondary: '#fff' },
    ...options,
  });

  activeToasts.set(errorKey, toastId);

  setTimeout(() => {
    if (activeToasts.get(errorKey) === toastId) {
      activeToasts.delete(errorKey);
    }
  }, 4000);

  return toastId;
};

/**
 * Show a single "session expired" toast and suppress all others
 * for a few seconds so the user sees one message, not ten.
 */
export const showSessionExpiredToast = (
  message = 'Your session has expired. Please log in again.'
) => {
  suppressErrorToasts(5000);

  activeToasts.forEach((toastId) => toast.dismiss(toastId));
  activeToasts.clear();

  // Reset global cooldown so the session-expired toast fires cleanly
  lastErrorShownAt = Date.now();

  return toast.error(message, {
    id: 'session-expired',
    duration: 4000,
    position: 'top-right',
    style: {
      background: '#FEE2E2',
      color: '#991B1B',
      borderRadius: '12px',
      fontSize: '14px',
      padding: '12px 16px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      maxWidth: '380px',
    },
    iconTheme: { primary: '#DC2626', secondary: '#fff' },
  });
};

export const showSuccessToast = (message, options = {}) => {
  if (!message || message.trim() === '') return null;

  const key = `success_${normalizeKey(message)}`;
  if (activeToasts.has(key)) return activeToasts.get(key);

  const toastId = toast.success(message, {
    id: key,
    duration: 3000,
    position: 'top-right',
    style: {
      background: '#D1FAE5',
      color: '#065F46',
      borderRadius: '12px',
      fontSize: '14px',
      padding: '12px 16px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      maxWidth: '380px',
    },
    iconTheme: { primary: '#10B981', secondary: '#fff' },
    ...options,
  });

  activeToasts.set(key, toastId);
  setTimeout(() => {
    if (activeToasts.get(key) === toastId) activeToasts.delete(key);
  }, 3000);

  return toastId;
};

export const showInfoToast = (message, options = {}) => {
  if (!message || message.trim() === '') return null;

  const key = `info_${normalizeKey(message)}`;
  if (activeToasts.has(key)) return activeToasts.get(key);

  const toastId = toast(message, {
    id: key,
    duration: 3000,
    position: 'top-right',
    icon: 'ℹ️',
    style: {
      background: '#DBEAFE',
      color: '#1E40AF',
      borderRadius: '12px',
      fontSize: '14px',
      padding: '12px 16px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      maxWidth: '380px',
    },
    ...options,
  });

  activeToasts.set(key, toastId);
  setTimeout(() => {
    if (activeToasts.get(key) === toastId) activeToasts.delete(key);
  }, 3000);

  return toastId;
};

export const showLoadingToast = (message = 'Processing...', options = {}) => {
  return toast.loading(message, {
    position: 'top-right',
    style: {
      background: '#F3F4F6',
      color: '#374151',
      borderRadius: '12px',
      fontSize: '14px',
      padding: '12px 16px',
      maxWidth: '380px',
    },
    ...options,
  });
};

/**
 * Extract the user-friendly message from an Axios error object.
 * Priority: data.message → data.error (string) → data.error.message
 */
export const extractBackendMessage = (axiosError) => {
  const data = axiosError?.response?.data;
  if (!data) return null;

  // 1. New shape
  if (typeof data.message === 'string' && data.message.trim().length > 0) {
    const m = data.message;
    if (
      !m.includes('Request failed') &&
      !m.includes('status code') &&
      !m.includes('ECONN') &&
      !m.includes('ERR_') &&
      m.length < 300
    ) {
      return m;
    }
  }

  // 2. Old shape — string
  if (typeof data.error === 'string' && data.error.trim().length > 0) {
    return data.error;
  }

  // 3. Old shape — nested
  if (data.error && typeof data.error === 'object' && typeof data.error.message === 'string') {
    return data.error.message;
  }

  return null;
};

/**
 * Fallback — map by status code only when backend sent no message.
 */
export const getFriendlyErrorMessage = (status, backendMessage = null) => {
  if (
    backendMessage &&
    typeof backendMessage === 'string' &&
    backendMessage.length > 0 &&
    backendMessage.length < 300
  ) {
    if (
      !backendMessage.includes('Request failed') &&
      !backendMessage.includes('status code') &&
      !backendMessage.includes('ECONN') &&
      !backendMessage.includes('ERR_')
    ) {
      return backendMessage;
    }
  }

  switch (status) {
    case 400: return 'Please check your input and try again.';
    case 401: return 'Your session has expired. Please log in again.';
    case 403: return 'You don\'t have permission to do that.';
    case 404: return 'The requested item was not found.';
    case 409: return 'This item already exists.';
    case 413: return 'The data you sent is too large. Please try a smaller file.';
    case 422: return 'Some details are incorrect. Please check and try again.';
    case 429: return 'Too many requests. Please wait a moment and try again.';
    case 500:
    case 502:
    case 503:
    case 504: return 'Something went wrong on our end. Please try again in a moment.';
    default: return null;
  }
};

export const clearAllToasts = () => {
  activeToasts.forEach((toastId) => toast.dismiss(toastId));
  activeToasts.clear();
};

export const dismissToast = (toastId) => {
  if (toastId) toast.dismiss(toastId);
};

export default {
  showErrorToast,
  showSuccessToast,
  showInfoToast,
  showLoadingToast,
  showSessionExpiredToast,
  suppressErrorToasts,
  clearSuppression,
  isSuppressed,
  extractBackendMessage,
  getFriendlyErrorMessage,
  clearAllToasts,
  dismissToast,
};